import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * État de la connexion réseau du navigateur (MOKA-349).
 *
 * - `online()` : signal, vrai tant que le navigateur se croit connecté (événements
 *   `online` / `offline` de la fenêtre) ;
 * - `reconnected$` : émet à chaque retour de la connexion après une coupure ;
 * - la coquille (`app.component`) en tire le bandeau « Vous êtes hors ligne » et le toast
 *   « Connexion rétablie ».
 *
 * `navigator.onLine` ne détecte qu'une absence de réseau local (wifi coupé, mode avion), pas un
 * serveur injoignable : une requête qui échoue garde donc son propre message d'erreur (avec
 * « Réessayer » : `NotificationsService.showError(msg, { action })`).
 * L'écran de la tablette d'une salle (`room-display`) suit sa propre connexion et n'en dépend pas.
 */
@Injectable({ providedIn: 'root' })
export class ConnectivityService {
  private readonly state = signal(readOnline());
  private readonly back = new Subject<void>();

  /** Vrai quand le navigateur est connecté. */
  readonly online = this.state.asReadonly();
  /** Vrai quand le navigateur est hors ligne. */
  readonly offline = computed(() => !this.state());
  /** Émet au retour de la connexion (jamais au chargement). */
  readonly reconnected$: Observable<void> = this.back.asObservable();

  constructor() {
    if (typeof window === 'undefined') return;
    const goOnline = () => this.setOnline(true);
    const goOffline = () => this.setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    inject(DestroyRef).onDestroy(() => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    });
  }

  private setOnline(value: boolean): void {
    const was = this.state();
    this.state.set(value);
    if (!was && value) this.back.next();
  }
}

function readOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}
